import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogContentTextProps,
  DialogTitle,
  DialogTitleProps,
  FormControl,
  FormControlProps,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  StackProps,
  SxProps,
  TextFieldProps,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import {
  Close as CloseIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import {
  LocalizationProvider,
  MobileDatePicker,
  CalendarPicker,
  LoadingButton,
} from '@mui/lab';
import AdapterMoment from '@mui/lab/AdapterMoment';
import moment, { Moment } from 'moment';
import { TP_COLORS } from '../../../../constants';
import { useAppSelector } from '../../../../hooks';

import { getAuthState } from '../../../../store/AuthSlice';
import { createTrial, getTrials } from '../../../../store/V2/TrialsSlice';
import {
  getUserAvailabilitySlotsForTrial,
  getUserAvailabilityState,
  resetUserAvailabilitySlotOptionsForTrial,
  TUserAvailabilitySlotOption,
} from '../../../../store/V2/UserAvailabilitySlice';
import {
  getCurriculumsV2,
  getCurriculumV2State,
} from '../../../../store/V2/CurriculumSlice';

import BasicLoader from '../../../../components/Loaders/BasicLoader';
import {
  TPTextField,
  TPAutoCompleteArrowIconComponent,
} from '../../../../components/Inputs';

const dialogTitleProps: DialogTitleProps = {
  sx: {
    padding: '0px',
    fontWeight: '600',
    fontSize: '16px',
    marginBottom: '10px',
  },
};

const dialogContentTextProps: DialogContentTextProps = {
  color: TP_COLORS.gray.light,
  fontSize: '14px',
  marginBottom: '10px',
};

const actionButtonStyles: SxProps = {
  borderRadius: '6px',
  fontWeight: 600,
  fontSize: '12px',
  width: '122px',
  height: '40px',
};

const formControlProps: FormControlProps = {
  fullWidth: true,
  sx: { marginBottom: '20px' },
};

const slotStackProps: StackProps = {
  alignItems: 'center',
  justifyContent: 'center',
  height: '150px',
};

type TSelectOption = {
  label: string;
  value: string;
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

type TState = {
  selectedCurriculum: TSelectOption | null;
  selectedSubject: TSelectOption | null;
  selectedGrade: TSelectOption | null;
  selectedDate: Moment;
  selectedSlot?: TUserAvailabilitySlotOption | undefined;
};

const initialState: TState = {
  selectedCurriculum: null,
  selectedSubject: null,
  selectedGrade: null,
  selectedDate: moment(),
  selectedSlot: undefined,
};

interface BookTrialModalProps {
  open: boolean;
  onClose: () => void;
}

const BookTrialModal: React.FC<BookTrialModalProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  // Store
  const authState = useAppSelector(getAuthState);
  const curriculumState = useAppSelector(getCurriculumV2State);
  const userAvailabilityState = useAppSelector(getUserAvailabilityState);

  // State
  const [state, setState] = useState(initialState);
  const [isLoading, setLoading] = useState(false);
  const [gradeOptions, setGradeOptions] = useState<Array<TSelectOption>>([]);
  const [subjectOptions, setSubjectOptions] = useState<Array<TSelectOption>>(
    []
  );
  const [curriculumOptions, setCurriculumOptions] = useState<
    Array<TSelectOption>
  >([]);

  const { userAvailabilitySlotOptionsForTrial } = userAvailabilityState;

  const isSlotsLoading = userAvailabilityState.status === 'loading';
  const isSubmitDisabled =
    !state.selectedCurriculum ||
    !state.selectedSubject ||
    !state.selectedGrade ||
    !state.selectedDate ||
    !state.selectedSlot ||
    authState.status === 'loading';

  useEffect(() => {
    dispatch(getCurriculumsV2());

    return () => {
      dispatch(resetUserAvailabilitySlotOptionsForTrial());
    };
  }, []);

  useEffect(() => {
    if (curriculumState.list && curriculumState.list.length) {
      setCurriculumOptions(
        curriculumState.list.map((curriculumItem) => ({
          label: curriculumItem.curriculum,
          value: curriculumItem.curriculum,
        }))
      );

      const matchingCurriculum = curriculumState.list.find(
        (curriculumItem) =>
          curriculumItem.curriculum === state.selectedCurriculum?.value
      );

      if (
        matchingCurriculum &&
        matchingCurriculum.subjects &&
        matchingCurriculum.subjects.length
      ) {
        const newSubjectOptions: TSelectOption[] = [];

        matchingCurriculum.subjects.forEach((subjectItem) => {
          newSubjectOptions.push({
            label: subjectItem.subject,
            value: subjectItem.subject,
          });
        });

        setSubjectOptions(newSubjectOptions);

        const matchingSubject = matchingCurriculum.subjects.find(
          (subjectItem) => subjectItem.subject === state.selectedSubject?.value
        );

        if (
          matchingSubject &&
          matchingSubject.grade &&
          matchingSubject.grade.length
        ) {
          const newGradeOptions: TSelectOption[] = [];

          matchingSubject.grade.forEach((gradeItem) => {
            newGradeOptions.push({
              label: gradeItem.grade,
              value: gradeItem.grade,
            });
          });

          setGradeOptions(newGradeOptions);
        }
      }
    }
  }, [curriculumState.list]);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const handleDateChange = (date: Moment | null) => {
    if (date) {
      updateStateAttributes({ selectedDate: date, selectedSlot: undefined });

      if (
        state.selectedCurriculum &&
        state.selectedSubject &&
        state.selectedGrade
      ) {
        dispatch(
          getUserAvailabilitySlotsForTrial({
            date: date.toISOString(),
            curriculum: state.selectedCurriculum.value,
            subject: state.selectedSubject.value,
            grade: state.selectedGrade.value,
          })
        );
      }
    }
  };

  const handleCurriculumChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    const newSubjectOptions: TSelectOption[] = [];

    if (curriculumState.list && curriculumState.list.length && value) {
      const selectedCurriculumData = curriculumState.list.find(
        (item) => item.curriculum === value.value
      );

      if (
        selectedCurriculumData &&
        selectedCurriculumData.subjects &&
        selectedCurriculumData.subjects.length
      ) {
        selectedCurriculumData.subjects.forEach((item) => {
          newSubjectOptions.push({
            label: item.subject,
            value: item.subject,
          });
        });
      }
    }

    setState((prevState) => ({
      ...prevState,
      selectedCurriculum: value,
      selectedSubject: null,
      selectedGrade: null,
    }));

    setSubjectOptions(newSubjectOptions);
    setGradeOptions([]);
  };

  const handleSubjectChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    const newGradeOptions: TSelectOption[] = [];

    if (state.selectedCurriculum) {
      const selectedCurriculumData = curriculumState.list.find(
        (curriculumItem) =>
          curriculumItem.curriculum === state.selectedCurriculum?.value
      );

      if (
        selectedCurriculumData &&
        selectedCurriculumData.subjects &&
        selectedCurriculumData.subjects.length
      ) {
        const selectedSubject = selectedCurriculumData.subjects.find(
          (subjectItem) => subjectItem.subject === value?.value
        );

        if (
          selectedSubject &&
          selectedSubject.grade &&
          selectedSubject.grade.length
        ) {
          selectedSubject.grade.forEach((gradeItem) => {
            newGradeOptions.push({
              label: gradeItem.grade,
              value: gradeItem.grade,
            });
          });
        }
      }
    }

    setState((prevState) => ({
      ...prevState,
      selectedSubject: value,
      selectedGrade: null,
    }));

    setGradeOptions(newGradeOptions);
  };

  const handleGradeChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      selectedGrade: value,
    }));

    if (state.selectedCurriculum && state.selectedSubject && value) {
      dispatch(
        getUserAvailabilitySlotsForTrial({
          date: state.selectedDate.toISOString(),
          curriculum: state.selectedCurriculum.value,
          subject: state.selectedSubject.value,
          grade: value.value,
        })
      );
    }
  };

  const handleSubmit = async () => {
    if (
      state.selectedCurriculum &&
      state.selectedSubject &&
      state.selectedGrade &&
      state.selectedSlot
    ) {
      setLoading(true);
      await dispatch(
        createTrial({
          curriculum: state.selectedCurriculum.value,
          subject: state.selectedSubject.value,
          grade: state.selectedGrade.value,
          startDate: moment(state.selectedSlot.start).toISOString(),
          endDate: state.selectedSlot.end,
          tutorId: state.selectedSlot.user || '',
        })
      );
      setLoading(false);
      dispatch(getTrials());
      onClose();
    }
  };

  return (
    <Dialog
      maxWidth="lg"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          padding: '30px',
          borderRadius: '20px',
          width: '1100px',
        },
      }}
    >
      <DialogTitle {...dialogTitleProps}>Book your FREE Class</DialogTitle>
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', md: '12px' },
          top: { xs: '7px', md: '12px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent sx={{ padding: '0px' }}>
        <DialogContentText {...dialogContentTextProps}>
          We are with you on your learning journey, you are just one step closer
          to booking your free trial.
        </DialogContentText>

        <Grid container columnGap="30px" rowGap="20px">
          <Grid
            item
            xs={12}
            md={6}
            lg
            sx={{
              width: '390px',
              marginTop: '25px',
              maxWidth: { md: '48%' },
            }}
          >
            <Stack>
              <FormControl {...formControlProps}>
                <Autocomplete
                  size="small"
                  value={state.selectedCurriculum}
                  options={curriculumOptions}
                  onChange={handleCurriculumChange}
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  loading={curriculumState.status === 'loading'}
                  isOptionEqualToValue={(option, value) =>
                    option.label === value.value
                  }
                  getOptionKey={(option: TSelectOption) => option.value}
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      label="Curriculum *"
                      placeholder="Curriculum"
                    />
                  )}
                />
              </FormControl>

              <FormControl {...formControlProps}>
                <Autocomplete
                  size="small"
                  value={state.selectedSubject}
                  options={subjectOptions}
                  onChange={handleSubjectChange}
                  disabled={!state.selectedCurriculum}
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  loading={curriculumState.status === 'loading'}
                  isOptionEqualToValue={(option, value) =>
                    option.label === value.value
                  }
                  getOptionKey={(option: TSelectOption) => option.value}
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      label="Subject *"
                      placeholder="Subject"
                    />
                  )}
                />
              </FormControl>

              <FormControl {...formControlProps}>
                <Autocomplete
                  size="small"
                  value={state.selectedGrade}
                  options={gradeOptions}
                  onChange={handleGradeChange}
                  disabled={!state.selectedSubject}
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  loading={curriculumState.status === 'loading'}
                  isOptionEqualToValue={(option, value) =>
                    option.label === value.value
                  }
                  getOptionKey={(option: TSelectOption) => option.value}
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      label="Grade *"
                      placeholder="Grade"
                    />
                  )}
                />
              </FormControl>
              {!isDesktop ? (
                <FormControl {...formControlProps}>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <MobileDatePicker
                      disabled={isSlotsLoading}
                      value={state.selectedDate}
                      minDate={moment()}
                      onChange={handleDateChange}
                      label="Date *"
                      inputFormat="YYYY MMM DD"
                      renderInput={(params) => (
                        <TPTextField
                          {...params}
                          {...textFieldProps}
                          size="small"
                          placeholder="Date"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <InputAdornment position="end">
                                <DateRangeIcon />
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </FormControl>
              ) : null}
            </Stack>
          </Grid>
          {isDesktop ? (
            <Grid item xs={12} lg style={{ width: '300px' }}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <Box
                  sx={{
                    '& .css-dhopo2': {
                      minHeight: '200px',
                    },
                  }}
                >
                  <CalendarPicker
                    disabled={isSlotsLoading}
                    views={['day']}
                    minDate={moment()}
                    date={state.selectedDate}
                    onChange={handleDateChange}
                  />
                </Box>
              </LocalizationProvider>
            </Grid>
          ) : null}
          <Grid
            item
            xs={12}
            md={6}
            lg
            sx={{
              // maxHeight: '300px',
              // overflowY: 'auto',
              maxWidth: { md: '48%' },
              marginTop: { md: '20px', lg: '0px' },
              marginRight: '10px',
            }}
          >
            <DialogTitle {...dialogTitleProps}>Select your slot</DialogTitle>
            <div>
              {!isSlotsLoading &&
              userAvailabilitySlotOptionsForTrial &&
              userAvailabilitySlotOptionsForTrial.length ? (
                <Grid container spacing="10px">
                  {userAvailabilitySlotOptionsForTrial.map(
                    (slotItem, index) => {
                      const isSelected =
                        state.selectedSlot &&
                        JSON.stringify(state.selectedSlot) ===
                          JSON.stringify(slotItem);

                      return (
                        <Grid key={`slot-${index}`} item xs={6}>
                          <Card
                            elevation={0}
                            sx={{
                              border: '1px solid #01A79F',
                              borderRadius: '.375rem',
                              backgroundColor: isSelected
                                ? '#01A79F'
                                : undefined,
                            }}
                          >
                            <CardActionArea
                              onClick={() =>
                                updateStateAttributes({
                                  selectedSlot: slotItem,
                                })
                              }
                            >
                              <CardContent sx={{ p: '.5rem 1rem' }}>
                                <Typography
                                  fontSize="14px"
                                  fontWeight={500}
                                  color={isSelected ? '#ffffff' : '#01A79F'}
                                  textAlign="center"
                                >
                                  {moment(slotItem.start).format('hh:mm A')}
                                </Typography>
                              </CardContent>
                            </CardActionArea>
                          </Card>
                        </Grid>
                      );
                    }
                  )}
                </Grid>
              ) : null}
              {!userAvailabilitySlotOptionsForTrial && !isSlotsLoading ? (
                <Stack {...slotStackProps}>
                  <Typography fontSize="14px">
                    Select curriculum, subject and grade
                  </Typography>
                </Stack>
              ) : null}
              {isSlotsLoading ? (
                <Stack {...slotStackProps}>
                  <BasicLoader loaderProps={{ size: 30 }} />
                </Stack>
              ) : null}
              {!isSlotsLoading &&
              userAvailabilitySlotOptionsForTrial &&
              !userAvailabilitySlotOptionsForTrial.length ? (
                <Stack {...slotStackProps}>
                  <Typography fontSize="14px">
                    No slots are available
                  </Typography>
                </Stack>
              ) : null}
            </div>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ padding: '0px' }}>
        <Button
          disableElevation
          variant="contained"
          color="inherit"
          onClick={onClose}
          sx={{
            ...actionButtonStyles,
            backgroundColor: TP_COLORS.border.color1,
          }}
        >
          Dismiss
        </Button>

        <LoadingButton
          disableElevation
          variant="contained"
          loading={isLoading}
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          sx={actionButtonStyles}
        >
          Book Trial
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default BookTrialModal;
